import { useState, useEffect, useRef, useMemo } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { 
  ChevronDown, 
  ChevronUp, 
  Play,
  Pause,
  RotateCcw,
  Check
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";

// Chart styling constants
const CHART_AXIS_TICK_STYLE = { fill: '#a69373', fontSize: 10 };
const CHART_Y_AXIS_DOMAIN = [0, 5];

// ============ SVG ICONS ============
const MoodFaceIcon = ({ mood, selected }) => {
  const colors = {
    1: { fill: selected ? "#c97b6e" : "#5a4a4a", bg: selected ? "#c97b6e20" : "transparent" },
    2: { fill: selected ? "#d4956e" : "#5a5040", bg: selected ? "#d4956e20" : "transparent" },
    3: { fill: selected ? "#a0a0a0" : "#505050", bg: selected ? "#a0a0a020" : "transparent" },
    4: { fill: selected ? "#7b9e7b" : "#4a5a4a", bg: selected ? "#7b9e7b20" : "transparent" },
    5: { fill: selected ? "#a69373" : "#5a5040", bg: selected ? "#a6937320" : "transparent" }
  };
  const { fill, bg } = colors[mood];
  
  const faces = {
    1: ( // Very sad
      <svg viewBox="0 0 40 40" className="w-10 h-10">
        <circle cx="20" cy="20" r="18" fill={bg} stroke={fill} strokeWidth="2"/>
        <circle cx="14" cy="16" r="2" fill={fill}/>
        <circle cx="26" cy="16" r="2" fill={fill}/>
        <path d="M12 28 Q20 22 28 28" stroke={fill} strokeWidth="2" fill="none" strokeLinecap="round"/>
      </svg>
    ),
    2: ( // Sad
      <svg viewBox="0 0 40 40" className="w-10 h-10">
        <circle cx="20" cy="20" r="18" fill={bg} stroke={fill} strokeWidth="2"/>
        <circle cx="14" cy="16" r="2" fill={fill}/>
        <circle cx="26" cy="16" r="2" fill={fill}/>
        <path d="M14 27 Q20 24 26 27" stroke={fill} strokeWidth="2" fill="none" strokeLinecap="round"/>
      </svg>
    ),
    3: ( // Neutral
      <svg viewBox="0 0 40 40" className="w-10 h-10">
        <circle cx="20" cy="20" r="18" fill={bg} stroke={fill} strokeWidth="2"/>
        <circle cx="14" cy="16" r="2" fill={fill}/>
        <circle cx="26" cy="16" r="2" fill={fill}/>
        <line x1="14" y1="26" x2="26" y2="26" stroke={fill} strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    4: ( // Happy
      <svg viewBox="0 0 40 40" className="w-10 h-10">
        <circle cx="20" cy="20" r="18" fill={bg} stroke={fill} strokeWidth="2"/>
        <circle cx="14" cy="16" r="2" fill={fill}/>
        <circle cx="26" cy="16" r="2" fill={fill}/>
        <path d="M14 24 Q20 28 26 24" stroke={fill} strokeWidth="2" fill="none" strokeLinecap="round"/>
      </svg>
    ),
    5: ( // Very happy
      <svg viewBox="0 0 40 40" className="w-10 h-10">
        <circle cx="20" cy="20" r="18" fill={bg} stroke={fill} strokeWidth="2"/>
        <circle cx="14" cy="15" r="2" fill={fill}/>
        <circle cx="26" cy="15" r="2" fill={fill}/>
        <path d="M12 23 Q20 30 28 23" stroke={fill} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
      </svg>
    )
  };
  return faces[mood];
};

const FeatherIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 text-[#a69373]" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="16" y1="8" x2="2" y2="22" strokeLinecap="round"/>
    <line x1="17.5" y1="15" x2="9" y2="15" strokeLinecap="round"/>
  </svg>
);

const LightningIcon = ({ filled }) => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill={filled ? "#a69373" : "none"} stroke="#a69373" strokeWidth="2">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" strokeLinejoin="round" strokeLinecap="round"/>
  </svg>
);

const FlameIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="#a69373" stroke="#a69373" strokeWidth="1">
    <path d="M12 2c0 4-4 6-4 10a4 4 0 0 0 8 0c0-4-4-6-4-10z"/>
    <path d="M12 12c0 2-1.5 3-1.5 4.5a1.5 1.5 0 0 0 3 0c0-1.5-1.5-2.5-1.5-4.5z" fill="#0B2821"/>
  </svg>
);

const SmileIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="#a69373" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <path d="M8 14s1.5 2 4 2 4-2 4-2" strokeLinecap="round"/>
    <line x1="9" y1="9" x2="9.01" y2="9" strokeLinecap="round"/>
    <line x1="15" y1="9" x2="15.01" y2="9" strokeLinecap="round"/>
  </svg>
);

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="#a69373" strokeWidth="2">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="#a69373" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="#a69373" strokeWidth="2">
    <polyline points="23 4 23 10 17 10" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="1 20 1 14 7 14" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ZapIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="#a69373" strokeWidth="2">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" strokeLinejoin="round" strokeLinecap="round"/>
  </svg>
);

// ============ MOOD TRACKER ============
const MOODS = [
  { value: 1, label: "Very Low" },
  { value: 2, label: "Low" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Good" },
  { value: 5, label: "Great" }
];

export function MoodTracker({ isExpanded, onToggle }) {
  const { token } = useAuth();
  const [selectedMood, setSelectedMood] = useState(null);
  const [moodHistory, setMoodHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isExpanded) loadMoodHistory();
  }, [isExpanded]);

  const loadMoodHistory = async () => {
    try {
      const res = await axios.get(`${API}/journal?limit=30`, { withCredentials: true });
      const moods = (res.data || [])
        .filter(e => e.tags?.includes('mood-check'))
        .slice(0, 7)
        .map(e => ({
          date: new Date(e.created_at).toLocaleDateString('en-US', { weekday: 'short' }),
          mood: parseInt(e.body) || 3
        }));
      setMoodHistory(moods.reverse());
      
      const today = new Date().toDateString();
      const todayEntry = (res.data || []).find(e => 
        new Date(e.created_at).toDateString() === today && e.tags?.includes('mood-check')
      );
      if (todayEntry) setSelectedMood(parseInt(todayEntry.body));
    } catch (err) {
      console.error("Failed to load mood history:", err);
    }
  };

  const saveMood = async (moodValue) => {
    setSelectedMood(moodValue);
    setSaving(true);
    try {
      await axios.post(`${API}/journal`, {
        title: `Mood Check - ${new Date().toLocaleDateString()}`,
        body: String(moodValue),
        mood: MOODS.find(m => m.value === moodValue)?.label.toLowerCase() || "neutral",
        tags: ["mood-check", "wellness"],
        source: "mood_tracker"
      }, { withCredentials: true });
      toast.success("Mood logged!");
      loadMoodHistory();
    } catch (err) {
      toast.error("Failed to save mood");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="glass-card overflow-hidden">
      <CardContent className="p-0">
        <button 
          className="w-full p-4 flex items-center justify-between text-left"
          onClick={onToggle}
          data-testid="mood-tracker-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#a69373]/20 flex items-center justify-center">
              <SmileIcon />
            </div>
            <div>
              <h3 className="font-bold" style={{ color: '#a69373' }}>Mood Tracker</h3>
              <p className="text-xs" style={{ color: 'rgba(166, 147, 115, 0.6)' }}>Check in with how you're feeling</p>
            </div>
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-[#a69373]/50" /> : <ChevronDown className="w-5 h-5 text-[#a69373]/50" />}
        </button>
        
        {isExpanded && (
          <div className="px-4 pb-4 space-y-4 animate-fade-in">
            <div className="flex justify-center gap-3">
              {MOODS.map((mood) => (
                <button
                  key={mood.value}
                  onClick={() => saveMood(mood.value)}
                  disabled={saving}
                  className={`p-1 rounded-xl transition-all ${
                    selectedMood === mood.value ? "scale-110 ring-2 ring-[#a69373]" : "hover:scale-105"
                  }`}
                  data-testid={`mood-${mood.value}`}
                >
                  <MoodFaceIcon mood={mood.value} selected={selectedMood === mood.value} />
                </button>
              ))}
            </div>
            
            {moodHistory.length > 0 && (
              <div>
                <p className="text-[#a69373]/50 text-xs text-center mb-2">Last 7 days</p>
                <div className="flex justify-center gap-3">
                  {moodHistory.map((day) => (
                    <div key={day.date} className="text-center">
                      <MoodFaceIcon mood={day.mood} selected={false} />
                      <p className="text-[#a69373]/40 text-xs mt-1">{day.date}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============ GRATITUDE PROMPT ============
const GRATITUDE_PROMPTS = [
  "Name 3 things you are grateful for today",
  "What small moment brought you joy recently?",
  "Who has helped you lately, and why are you thankful?",
  "What ability or skill are you grateful to have?",
  "What challenge helped you grow recently?",
  "What beauty did you notice today?",
  "What comfort do you often take for granted?",
  "What relationship enriches your life?",
  "What recent experience are you thankful for?",
  "What part of your daily routine brings you peace?"
];

export function GratitudePrompt({ isExpanded, onToggle }) {
  const { token } = useAuth();
  const [response, setResponse] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  const todaysPrompt = GRATITUDE_PROMPTS[dayOfYear % GRATITUDE_PROMPTS.length];

  const saveGratitude = async () => {
    if (!response.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/journal`, {
        title: `Gratitude - ${new Date().toLocaleDateString()}`,
        body: `**Prompt:** ${todaysPrompt}\n\n${response}`,
        mood: "grateful",
        tags: ["gratitude", "reflection", "wellness"],
        source: "gratitude_prompt"
      }, { withCredentials: true });
      toast.success("Gratitude saved to journal!");
      setSaved(true);
      setResponse("");
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="glass-card overflow-hidden">
      <CardContent className="p-0">
        <button 
          className="w-full p-4 flex items-center justify-between text-left"
          onClick={onToggle}
          data-testid="gratitude-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#a69373]/20 flex items-center justify-center">
              <HeartIcon />
            </div>
            <div>
              <h3 className="font-bold" style={{ color: '#a69373' }}>Gratitude Prompt</h3>
              <p className="text-[#a69373]/50 text-xs">Cultivate appreciation daily</p>
            </div>
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-[#a69373]/50" /> : <ChevronDown className="w-5 h-5 text-[#a69373]/50" />}
        </button>
        
        {isExpanded && (
          <div className="px-4 pb-4 space-y-3 animate-fade-in">
            <div className="p-3 bg-[#a69373]/10 rounded-xl flex items-start gap-2">
              <FeatherIcon />
              <p className="text-[#a69373] text-sm font-medium">{todaysPrompt}</p>
            </div>
            
            {saved ? (
              <div className="flex items-center gap-2 p-3 bg-[#7b9e7b]/20 rounded-xl">
                <Check className="w-5 h-5 text-[#7b9e7b]" />
                <span className="text-[#7b9e7b] text-sm">Saved to your journal!</span>
              </div>
            ) : (
              <>
                <Textarea
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  placeholder="Write your gratitude..."
                  className="bg-white/10 border-[#a69373]/20 text-[#a69373] placeholder:text-[#a69373]/30 min-h-[80px]"
                />
                <Button 
                  onClick={saveGratitude} 
                  disabled={saving || !response.trim()}
                  className="w-full btn-primary"
                >
                  {saving ? "Saving..." : "Save to Journal"}
                </Button>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============ FOCUS TIMER ============
export function FocusTimer({ isExpanded, onToggle }) {
  const [timeLeft, setTimeLeft] = useState(25 * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [isBreak, setIsBreak] = useState(false);
  const [sessions, setSessions] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(t => t - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      clearInterval(intervalRef.current);
      if (!isBreak) {
        setSessions(s => s + 1);
        setIsBreak(true);
        setTimeLeft(5 * 60);
        toast.success("Work session complete! Take a 5-minute break.");
      } else {
        setIsBreak(false);
        setTimeLeft(25 * 60);
        toast.success("Break over! Ready for another session?");
      }
      setIsRunning(false);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning, timeLeft, isBreak]);

  const toggleTimer = () => setIsRunning(!isRunning);
  
  const resetTimer = () => {
    setIsRunning(false);
    clearInterval(intervalRef.current);
    setIsBreak(false);
    setTimeLeft(25 * 60);
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const progress = isBreak 
    ? ((5 * 60 - timeLeft) / (5 * 60)) * 100
    : ((25 * 60 - timeLeft) / (25 * 60)) * 100;

  return (
    <Card className="glass-card overflow-hidden">
      <CardContent className="p-0">
        <button 
          className="w-full p-4 flex items-center justify-between text-left"
          onClick={onToggle}
          data-testid="focus-timer-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#a69373]/20 flex items-center justify-center">
              <ClockIcon />
            </div>
            <div>
              <h3 className="font-bold" style={{ color: '#a69373' }}>Focus Timer</h3>
              <p className="text-[#a69373]/50 text-xs">Pomodoro technique: 25 min work, 5 min break</p>
            </div>
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-[#a69373]/50" /> : <ChevronDown className="w-5 h-5 text-[#a69373]/50" />}
        </button>
        
        {isExpanded && (
          <div className="px-4 pb-4 space-y-4 animate-fade-in">
            {/* Circular Timer with Pulsing Glow */}
            <div className="relative w-40 h-40 mx-auto">
              {/* Pulsing glow when running */}
              {isRunning && (
                <div 
                  className="absolute inset-0 rounded-full animate-pulse"
                  style={{
                    background: `radial-gradient(circle, ${isBreak ? 'rgba(123,158,123,0.3)' : 'rgba(201,169,110,0.3)'} 0%, transparent 70%)`,
                    animation: 'pulse 2s ease-in-out infinite'
                  }}
                />
              )}
              <svg className="w-full h-full transform -rotate-90 relative z-10">
                <circle
                  cx="80" cy="80" r="70"
                  stroke="rgba(201, 169, 110, 0.2)"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="80" cy="80" r="70"
                  stroke={isBreak ? "#7b9e7b" : "#a69373"}
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 70}
                  strokeDashoffset={2 * Math.PI * 70 * (1 - progress / 100)}
                  className="transition-all duration-1000"
                  style={{
                    filter: isRunning ? `drop-shadow(0 0 8px ${isBreak ? '#7b9e7b' : '#a69373'})` : 'none'
                  }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
                <span className="text-3xl font-heading text-[#a69373]">{formatTime(timeLeft)}</span>
                <span className="text-xs text-[#a69373]/50">{isBreak ? "Break" : "Focus"}</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={resetTimer}
                className="border-[#a69373]/30 text-[#a69373]"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
              <Button
                onClick={toggleTimer}
                className="btn-primary px-6"
              >
                {isRunning ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                {isRunning ? "Pause" : "Start"}
              </Button>
            </div>

            <p className="text-center text-[#a69373]/50 text-sm">
              Sessions today: <span className="font-bold" style={{ color: '#a69373' }}>{sessions}</span>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============ THOUGHT REFRAME TOOL ============
const REFRAME_QUESTIONS = [
  { id: 1, question: "What is the evidence for and against this thought?" },
  { id: 2, question: "What would you tell a friend who had this thought?" },
  { id: 3, question: "What is a more balanced way to see this?" }
];

export function ThoughtReframeTool({ isExpanded, onToggle }) {
  const { token } = useAuth();
  const [thought, setThought] = useState("");
  const [responses, setResponses] = useState({ 1: "", 2: "", 3: "" });
  const [showQuestions, setShowQuestions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSubmitThought = () => {
    if (thought.trim()) setShowQuestions(true);
  };

  const saveReframe = async () => {
    setSaving(true);
    try {
      const body = `**Original thought:** ${thought}\n\n` +
        REFRAME_QUESTIONS.map(q => `**${q.question}**\n${responses[q.id] || "(not answered)"}`).join("\n\n");
      
      await axios.post(`${API}/journal`, {
        title: `Thought Reframe - ${new Date().toLocaleDateString()}`,
        body,
        mood: "reflective",
        tags: ["thought-reframe", "cbt", "wellness"],
        source: "reframe_tool"
      }, { withCredentials: true });
      toast.success("Reframe saved to journal!");
      setSaved(true);
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setThought("");
    setResponses({ 1: "", 2: "", 3: "" });
    setShowQuestions(false);
    setSaved(false);
  };

  return (
    <Card className="glass-card overflow-hidden">
      <CardContent className="p-0">
        <button 
          className="w-full p-4 flex items-center justify-between text-left"
          onClick={onToggle}
          data-testid="thought-reframe-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#a69373]/20 flex items-center justify-center">
              <RefreshIcon />
            </div>
            <div>
              <h3 className="font-bold" style={{ color: '#a69373' }}>Thought Reframe</h3>
              <p className="text-[#a69373]/50 text-xs">Challenge and reframe negative thoughts</p>
            </div>
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-[#a69373]/50" /> : <ChevronDown className="w-5 h-5 text-[#a69373]/50" />}
        </button>
        
        {isExpanded && (
          <div className="px-4 pb-4 space-y-3 animate-fade-in">
            {saved ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-3 bg-[#7b9e7b]/20 rounded-xl">
                  <Check className="w-5 h-5 text-[#7b9e7b]" />
                  <span className="text-[#7b9e7b] text-sm">Saved to your journal!</span>
                </div>
                <Button onClick={reset} variant="outline" className="w-full border-[#a69373]/30 text-[#a69373]">
                  Reframe Another Thought
                </Button>
              </div>
            ) : !showQuestions ? (
              <>
                <Textarea
                  value={thought}
                  onChange={(e) => setThought(e.target.value)}
                  placeholder="Write a negative or anxious thought you're having..."
                  className="bg-white/10 border-[#a69373]/20 text-[#a69373] placeholder:text-[#a69373]/30 min-h-[60px]"
                />
                <Button 
                  onClick={handleSubmitThought} 
                  disabled={!thought.trim()}
                  className="w-full btn-primary"
                >
                  Reframe This Thought
                </Button>
              </>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-white/5 rounded-xl">
                  <p className="text-[#a69373]/70 text-xs mb-1">Your thought:</p>
                  <p className="text-[#a69373] text-sm italic">"{thought}"</p>
                </div>
                
                {REFRAME_QUESTIONS.map((q) => (
                  <div key={q.id}>
                    <p className="text-[#a69373] text-sm font-medium mb-1">{q.question}</p>
                    <Textarea
                      value={responses[q.id]}
                      onChange={(e) => setResponses({ ...responses, [q.id]: e.target.value })}
                      className="bg-white/10 border-[#a69373]/20 text-[#a69373] placeholder:text-[#a69373]/30 min-h-[50px] text-sm"
                    />
                  </div>
                ))}
                
                <Button 
                  onClick={saveReframe} 
                  disabled={saving}
                  className="w-full btn-primary"
                >
                  {saving ? "Saving..." : "Save to Journal"}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============ ENERGY CHECK-IN ============
export function EnergyCheckIn({ isExpanded, onToggle }) {
  const { token } = useAuth();
  const [selectedEnergy, setSelectedEnergy] = useState(null);
  const [energyHistory, setEnergyHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isExpanded) loadEnergyHistory();
  }, [isExpanded]);

  const loadEnergyHistory = async () => {
    try {
      const res = await axios.get(`${API}/journal?limit=30`, { withCredentials: true });
      const entries = (res.data || [])
        .filter(e => e.tags?.includes('energy-check'))
        .slice(0, 7)
        .map(e => ({
          date: new Date(e.created_at).toLocaleDateString('en-US', { weekday: 'short' }),
          energy: parseInt(e.body) || 3
        }));
      setEnergyHistory(entries.reverse());
      
      const today = new Date().toDateString();
      const todayEntry = (res.data || []).find(e => 
        new Date(e.created_at).toDateString() === today && e.tags?.includes('energy-check')
      );
      if (todayEntry) setSelectedEnergy(parseInt(todayEntry.body));
    } catch (err) {
      console.error("Failed to load energy history:", err);
    }
  };

  const saveEnergy = async (level) => {
    setSelectedEnergy(level);
    setSaving(true);
    try {
      await axios.post(`${API}/journal`, {
        title: `Energy Check - ${new Date().toLocaleDateString()}`,
        body: String(level),
        mood: level >= 4 ? "energized" : level >= 3 ? "neutral" : "tired",
        tags: ["energy-check", "wellness"],
        source: "energy_check"
      }, { withCredentials: true });
      toast.success("Energy logged!");
      loadEnergyHistory();
    } catch (err) {
      toast.error("Failed to save energy");
    } finally {
      setSaving(false);
    }
  };

  const chartData = energyHistory.map(d => ({ name: d.date, energy: d.energy }));

  return (
    <Card className="glass-card overflow-hidden">
      <CardContent className="p-0">
        <button 
          className="w-full p-4 flex items-center justify-between text-left"
          onClick={onToggle}
          data-testid="energy-checkin-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#a69373]/20 flex items-center justify-center">
              <ZapIcon />
            </div>
            <div>
              <h3 className="font-bold" style={{ color: '#a69373' }}>Energy Check-In</h3>
              <p className="text-[#a69373]/50 text-xs">Track your energy levels over time</p>
            </div>
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-[#a69373]/50" /> : <ChevronDown className="w-5 h-5 text-[#a69373]/50" />}
        </button>
        
        {isExpanded && (
          <div className="px-4 pb-4 space-y-4 animate-fade-in">
            {/* Energy Scale with Lightning Bolts */}
            <div className="flex justify-center gap-3">
              {[1, 2, 3, 4, 5].map((level) => (
                <button
                  key={level}
                  onClick={() => saveEnergy(level)}
                  disabled={saving}
                  className={`p-2 rounded-xl transition-all ${
                    selectedEnergy === level 
                      ? "bg-[#a69373]/20 scale-110 ring-2 ring-[#a69373]/50" 
                      : "hover:bg-white/10"
                  }`}
                  data-testid={`energy-${level}`}
                >
                  <LightningIcon filled={selectedEnergy !== null && level <= selectedEnergy} />
                </button>
              ))}
            </div>
            <div className="flex justify-between text-xs text-[#a69373]/40 px-4">
              <span>Very Low</span>
              <span>Very High</span>
            </div>
            
            {/* Energy Chart */}
            {energyHistory.length > 0 && (
              <div>
                <p className="text-[#a69373]/50 text-xs text-center mb-2">7-day trend</p>
                <div className="h-24">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <XAxis 
                        dataKey="name" 
                        tick={CHART_AXIS_TICK_STYLE}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis domain={CHART_Y_AXIS_DOMAIN} hide />
                      <Bar dataKey="energy" radius={[4, 4, 0, 0]}>
                        {chartData.map((entry) => (
                          <Cell 
                            key={entry.day} 
                            fill={entry.energy >= 4 ? '#7b9e7b' : entry.energy >= 3 ? '#a69373' : '#c97b6e'} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============ WELLNESS TOOLS ACCORDION ============
export function WellnessToolsAccordion() {
  const [expandedTool, setExpandedTool] = useState(null);

  const handleToggle = (toolId) => {
    setExpandedTool(expandedTool === toolId ? null : toolId);
  };

  return (
    <section className="space-y-3" data-testid="builtin-wellness-tools">
      <h2 className="font-heading text-white text-lg">Wellness Tools</h2>
      <MoodTracker 
        isExpanded={expandedTool === 'mood'} 
        onToggle={() => handleToggle('mood')} 
      />
      <GratitudePrompt 
        isExpanded={expandedTool === 'gratitude'} 
        onToggle={() => handleToggle('gratitude')} 
      />
      <FocusTimer 
        isExpanded={expandedTool === 'timer'} 
        onToggle={() => handleToggle('timer')} 
      />
      <ThoughtReframeTool 
        isExpanded={expandedTool === 'reframe'} 
        onToggle={() => handleToggle('reframe')} 
      />
      <EnergyCheckIn 
        isExpanded={expandedTool === 'energy'} 
        onToggle={() => handleToggle('energy')} 
      />
    </section>
  );
}
